import { api } from './api/apiClient';

/**
 * 팀 모니터링 API 클라이언트 (Sprint 6 Phase E + Sprint 7 드릴다운)
 */

export type MonitoringScope = 'me' | 'team' | 'global';
export type MonitoringKpi = 'pending' | 'overdue' | 'completed_today';

export interface PerAssignee {
  user_id: string;
  name: string;
  counts: {
    sales: number;
    purchase: number;
    production: number;
    claims: number;
    home_styling: number;
  };
}

export interface MonitoringOverview {
  scope: MonitoringScope;
  scope_id: string | null;
  per_assignee: PerAssignee[];
  totals: {
    pending: number;
    overdue: number;
    completed_today: number;
  };
}

export interface DrillDownItem {
  id: string;
  title: string;
  customer_name: string | null;
  status: string;
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  created_at: string;
  updated_at: string;
  age_days: number;
  overdue: boolean;
  detail_url: string;
}

export interface DrillDownGroup {
  menu: string;
  menu_label: string;
  count: number;
  items: DrillDownItem[];
}

export interface DrillDownPayload {
  kpi: MonitoringKpi;
  scope: MonitoringScope;
  total: number;
  groups: DrillDownGroup[];
}

export const monitoringService = {
  async getOverview(params: { scope: MonitoringScope; teamId?: string }): Promise<MonitoringOverview> {
    const q = new URLSearchParams({ scope: params.scope });
    if (params.teamId) q.append('teamId', params.teamId);
    const res = await api.get<{ success: boolean; data: MonitoringOverview }>(
      `/dashboard/monitoring/overview?${q.toString()}`,
    );
    return res.data || { scope: params.scope, scope_id: null, per_assignee: [], totals: { pending: 0, overdue: 0, completed_today: 0 } };
  },

  /**
   * KPI 카드 드릴다운 — 미처리/7일 초과/오늘 완료 항목 목록.
   */
  async getDrillDownItems(params: {
    kpi: MonitoringKpi;
    scope: MonitoringScope;
    teamId?: string;
    menu?: string;
    limit?: number;
  }): Promise<DrillDownPayload> {
    const q = new URLSearchParams({ kpi: params.kpi, scope: params.scope });
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.menu) q.append('menu', params.menu);
    if (params.limit) q.append('limit', String(params.limit));
    const res = await api.get<{ success: boolean; data: DrillDownPayload }>(
      `/dashboard/monitoring/items?${q.toString()}`,
    );
    return res.data || { kpi: params.kpi, scope: params.scope, total: 0, groups: [] };
  },

  /**
   * 선택된 항목의 상태를 일괄 변경. 메뉴별 기존 bulk endpoint 로 dispatch.
   * claims, home_styling 은 지원하지 않음 — UI 에서 사전 차단.
   */
  async bulkUpdateStatus(params: { menu: string; ids: string[]; newStatus: string }): Promise<{ success: number; failed: number }> {
    const { menu, ids, newStatus } = params;
    if (ids.length === 0) return { success: 0, failed: 0 };

    if (menu === 'sales') {
      await api.post(`/sales/orders/bulk-status-update`, { orderIds: ids, newStatus });
      return { success: ids.length, failed: 0 };
    }
    if (menu === 'purchase') {
      await api.patch(`/purchase/orders/bulk/status`, { orderIds: ids, newStatus });
      return { success: ids.length, failed: 0 };
    }
    if (menu === 'production') {
      await api.post(`/production/orders/bulk-status-update`, { orderIds: ids, newStatus });
      return { success: ids.length, failed: 0 };
    }
    throw new Error(`${menu} 메뉴는 일괄 상태 변경을 지원하지 않습니다.`);
  },

  /**
   * 선택된 항목 일괄 삭제 — 메뉴별 dispatch.
   * claims/home_styling 은 개별 DELETE 병렬 호출 (Promise.allSettled).
   */
  async bulkDelete(itemsByMenu: Record<string, string[]>): Promise<{ success: number; failed: number; errors: string[] }> {
    let success = 0, failed = 0;
    const errors: string[] = [];

    for (const [menu, ids] of Object.entries(itemsByMenu)) {
      if (ids.length === 0) continue;
      try {
        if (menu === 'sales') {
          await api.post(`/sales/orders/bulk-delete`, { orderIds: ids });
          success += ids.length;
        } else if (menu === 'purchase') {
          await api.delete(`/purchase/orders/bulk`, { data: { orderIds: ids } } as any);
          success += ids.length;
        } else if (menu === 'production') {
          // 주문(헤더) 삭제는 /production/orders — /items/bulk 는 line item 용이라 wrong.
          await api.delete(`/production/orders`, { data: { orderIds: ids } } as any);
          success += ids.length;
        } else {
          // claims, home_styling — 개별 DELETE 병렬
          const endpoint = menu === 'claims' ? '/claims' : '/home-styling';
          const results = await Promise.allSettled(ids.map((id) => api.delete(`${endpoint}/${id}`)));
          // eslint-disable-next-line no-loop-func -- forEach 는 동기 실행이라 안전
          results.forEach((r, i) => {
            if (r.status === 'fulfilled') success += 1;
            else { failed += 1; errors.push(`${menu}:${ids[i]} — ${(r.reason as Error)?.message || '실패'}`); }
          });
        }
      } catch (e: any) {
        failed += ids.length;
        errors.push(`${menu} — ${e?.message || '실패'}`);
      }
    }

    return { success, failed, errors };
  },
};

export default monitoringService;
